import { Permission } from "@octopusdeploy/octopus-server-client";
import type { Repository } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { AnalyticView } from "~/analytics/Analytics";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import { Form, useForm } from "~/components/form/Form/Form";
import { links } from "../../../../../../../packages/portal-routes/src/routes";
import { DeleteParentEnvironmentDialogLayout } from "./DeleteParentEnvironmentDialogLayout";
import { ParentEnvironmentForm } from "./ParentEnvironmentForm";
export async function parentEnvironmentPageLoader(repository: Repository, spaceId: string, environmentId: string): Promise<LoaderData> {
    const parentEnvironment = repository.ParentEnvironments.get({ Id: environmentId, SpaceId: spaceId });
    return {
        parentEnvironment: await parentEnvironment,
    };
}
interface LoaderData {
    parentEnvironment: ParentEnvironmentModel;
}
interface ParentEnvironmentPageProps {
    loaderData: LoaderData;
}
export interface ParentEnvironmentModel {
    SpaceId: string;
    Id: string;
    Name: string;
    Slug: string;
    Description?: string;
    SortOrder: number;
    UseGuidedFailure: boolean;
    AutomaticDeprovisioningRule: {
        ExpiryDays: number;
        ExpiryHours: number;
    };
}
export function ParentEnvironmentPage({ loaderData }: ParentEnvironmentPageProps) {
    const { navigate } = useSpaceAwareNavigation();
    const parentEnvironment = loaderData.parentEnvironment;
    const { model, setModel, formProps, createSaveAction, getFieldError } = useForm({
        formName: "Parent Environment",
        initialModel: loaderData.parentEnvironment,
        onSubmit: async (repository, model) => await repository.ParentEnvironments.modify(model),
        submitPermission: {
            permission: Permission.EnvironmentEdit,
            environment: parentEnvironment.Id,
        },
    });
    function handleDeleteConfirm() {
        navigate(links.environmentsPage.generateUrl({ spaceId: parentEnvironment.SpaceId }));
    }
    const overflowMenu = OverflowMenuConverterVNext.convertAll([
        OverflowMenuItems.dialogItem("Delete", <DeleteParentEnvironmentDialogLayout environment={parentEnvironment} onDelete={handleDeleteConfirm}/>, {
            permission: Permission.EnvironmentDelete,
            environment: "*",
        }),
    ]);
    return (<>
            <AnalyticView resource="ParentEnvironment"/>

            <PageContent header={{
            title: model.Name,
            primaryAction: createSaveAction(),
            breadcrumbs: [{ label: "Environments", pageUrl: links.environmentsPage.generateUrl({ spaceId: model.SpaceId }) }],
            overflowActions: overflowMenu.menuItems,
        }}>
                {overflowMenu.dialogs}
                <Form {...formProps}>
                    <ParentEnvironmentForm model={model} setModel={setModel} getFieldError={getFieldError}/>
                </Form>
            </PageContent>
        </>);
}
